import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  LinearProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';

/**
 * ChatInput Component
 *
 * Text input for sending messages with:
 * - Character counter (max 500)
 * - Submit on enter (or shift+enter for newline)
 * - Send button
 * - Disabled state while loading
 * - Touch-friendly design
 */

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  maxLength?: number;
  placeholder?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  maxLength = 500,
  placeholder = 'Type your message...',
}) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const characterCount = message.length;
  const isOverLimit = characterCount > maxLength;
  const isNearLimit = characterCount > maxLength * 0.8;
  const canSend = message.trim().length > 0 && !isOverLimit && !disabled;

  // Auto-focus on mount
  useEffect(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleSend = () => {
    if (canSend) {
      onSend(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Enter, allow Shift+Enter for newline
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Character Counter */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: isOverLimit
              ? 'error.main'
              : isNearLimit
              ? 'warning.main'
              : 'text.secondary',
            fontWeight: isOverLimit ? 600 : 400,
          }}
        >
          {characterCount}/{maxLength}
        </Typography>
      </Box>

      {/* Input Area */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          error={isOverLimit}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'background.default',
            },
          }}
          InputProps={{
            sx: {
              fontSize: '0.95rem',
              lineHeight: 1.5,
            },
          }}
        />

        <Tooltip title={!canSend ? 'Enter a message' : 'Send (Enter)'}>
          <span>
            <IconButton
              onClick={handleSend}
              disabled={!canSend}
              color="primary"
              sx={{
                bgcolor: canSend ? 'primary.main' : 'action.disabledBackground',
                color: canSend ? 'primary.contrastText' : 'action.disabled',
                '&:hover': {
                  bgcolor: canSend ? 'primary.dark' : 'action.disabledBackground',
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
                borderRadius: 2,
                p: 1,
              }}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Loading indicator */}
      {disabled && (
        <LinearProgress
          sx={{
            height: 2,
            borderRadius: 1,
            mt: 0.5,
          }}
        />
      )}
    </Box>
  );
};

export default ChatInput;
